// ────────────────────────────────────────────────────────────────────
// AssetStlViewer — vanilla three.js renderer for an asset's STL.
//
// Fetches the STL via authenticated axios (apiClient.assetStl) so the
// binary stream is auth-protected (not a public file). Parses with
// STLLoader, fits the camera to the geometry bounds, OrbitControls.
//
// Light + neutral aesthetic — matches the cream/navy XZYMODS palette.
// ────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Box, Loader2 } from "lucide-react";
import { apiClient, errMsg } from "@/lib/api";
import type { Asset } from "@/lib/types";

interface Props {
  asset: Asset;
}

export function AssetStlViewer({ asset }: Props): JSX.Element {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error" | "no-stl">(
    asset.has_stl ? "loading" : "no-stl",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!asset.has_stl) {
      setStatus("no-stl");
      return;
    }
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    setStatus("loading");
    setError(null);

    // ── Scene ────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#F4F6F9");

    const width  = mount.clientWidth;
    const height = mount.clientHeight;
    const camera = new THREE.PerspectiveCamera(38, width / height, 0.05, 5000);
    camera.position.set(8, 6, 8);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    mount.appendChild(renderer.domElement);

    // ── Lights ──────────────────────────────────────────────────
    scene.add(new THREE.AmbientLight(0xFBFAF5, 0.7));
    const dirKey = new THREE.DirectionalLight(0xFFF6E0, 0.95);
    dirKey.position.set(6, 12, 5);
    dirKey.castShadow = true;
    scene.add(dirKey);
    const dirFill = new THREE.DirectionalLight(0x7FB6FF, 0.3);
    dirFill.position.set(-6, 4, -8);
    scene.add(dirFill);
    const hemi = new THREE.HemisphereLight(0xFFFFFF, 0x4A5567, 0.3);
    scene.add(hemi);

    // ── Ground grid (subtle XZYMODS navy) ─────────────────────────
    const grid = new THREE.GridHelper(40, 40, 0x1F4E79, 0xB6BFCE);
    (grid.material as THREE.LineBasicMaterial).opacity = 0.25;
    (grid.material as THREE.LineBasicMaterial).transparent = true;
    scene.add(grid);

    // ── Controls ────────────────────────────────────────────────
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.target.set(0, 0, 0);

    // ── Load STL ────────────────────────────────────────────────
    apiClient.assetStl(asset.id)
      .then((buffer) => {
        if (cancelled) return;
        const loader = new STLLoader();
        const geom: THREE.BufferGeometry = loader.parse(buffer);
        geom.computeBoundingBox();
        geom.computeVertexNormals();

        // Center geometry at origin + scale to a sensible size
        const bbox = geom.boundingBox!;
        const center = bbox.getCenter(new THREE.Vector3());
        const size = bbox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        // Normalize so the largest dimension is ~10 units
        const scale = 10 / maxDim;
        geom.translate(-center.x, -center.y, -center.z);
        geom.scale(scale, scale, scale);

        const material = new THREE.MeshStandardMaterial({
          color: 0xA8B0BD,                 // mid neutral metal
          metalness: 0.45,
          roughness: 0.55,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geom, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);

        // Apply asset.stl_default_transform if present (Matrix4 JSON)
        try {
          if (asset.spec_updated_at) {
            // we don't have transform on asset directly · skip for now
          }
        } catch {}

        // Wireframe overlay — engineering feel
        const wire = new THREE.LineSegments(
          new THREE.WireframeGeometry(geom),
          new THREE.LineBasicMaterial({
            color: 0x1F4E79, transparent: true, opacity: 0.12, linewidth: 1,
          }),
        );
        scene.add(wire);

        // Fit camera to scene
        const scaledSize = size.clone().multiplyScalar(scale);
        const fitDist = Math.max(scaledSize.x, scaledSize.y, scaledSize.z) * 1.6;
        camera.position.set(fitDist * 0.7, fitDist * 0.55, fitDist * 0.7);
        controls.target.set(0, 0, 0);
        controls.update();

        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(errMsg(err));
        setStatus("error");
      });

    // ── Animate ─────────────────────────────────────────────────
    let raf = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ──────────────────────────────────────────────────
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      const w = e.contentRect.width;
      const h = e.contentRect.height;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(mount);

    // ── Cleanup ─────────────────────────────────────────────────
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry?.dispose();
        const mat = (obj as THREE.Mesh).material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else if (mat) (mat as THREE.Material).dispose();
      });
      try {
        mount.removeChild(renderer.domElement);
      } catch { /* unmounted */ }
    };
  }, [asset.id, asset.has_stl]);

  // ── No-STL placeholder ──────────────────────────────────────────
  if (status === "no-stl") {
    return (
      <div className="relative w-full aspect-[16/9] flex items-center justify-center
                      bg-gradient-to-br from-[#FBFAF5] to-[#EFE9DB]"
           style={{ border: "1px solid var(--hairline)" }}>
        <div className="text-center">
          <Box size={56} strokeWidth={1.2} style={{ color: "rgba(31,78,121,0.25)" }} className="mx-auto" />
          <div className="mt-3 font-mono uppercase"
               style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
            No STL uploaded for this asset
          </div>
          <div className="mt-1 font-mono"
               style={{ fontSize: 10, color: "var(--ink-4)" }}>
            Upload via Asset Studio (Phase 4)
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/9]"
         style={{ background: "#F4F6F9", border: "1px solid var(--hairline)" }}>
      <div ref={mountRef} className="absolute inset-0" />

      {/* Status overlay */}
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 px-3 py-1.5"
               style={{ background: "rgba(255,255,255,0.92)",
                        border: "1px solid var(--hairline)" }}>
            <Loader2 size={14} className="animate-spin" style={{ color: "var(--accent)" }} />
            <span className="font-mono uppercase"
                  style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
              Loading STL · v{asset.stl_version}
            </span>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="px-4 py-2 font-mono text-center"
               style={{ background: "rgba(255,255,255,0.95)",
                        border: "1px solid var(--err)", color: "var(--err)",
                        fontSize: 11, letterSpacing: ".12em" }}>
            <div className="uppercase mb-1" style={{ fontSize: 9.5, letterSpacing: ".22em" }}>
              STL load failed
            </div>
            <div style={{ color: "var(--ink-3)", textTransform: "none" }}>{error}</div>
          </div>
        </div>
      )}

      {/* Hint strip */}
      {status === "ready" && (
        <div className="absolute bottom-3 left-3 px-2.5 py-1.5 font-mono uppercase pointer-events-none flex items-center gap-2"
             style={{ background: "rgba(255,255,255,0.92)",
                      border: "1px solid var(--hairline)",
                      fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <span style={{ color: "var(--accent-deep)" }}>DRAG</span> rotate
          <span>·</span>
          <span style={{ color: "var(--accent-deep)" }}>WHEEL</span> zoom
          <span>·</span>
          <span style={{ color: "var(--accent-deep)" }}>RIGHT</span> pan
        </div>
      )}

      {/* STL version badge */}
      <div className="absolute top-3 right-3 px-2 py-1 font-mono uppercase pointer-events-none"
           style={{ background: "rgba(255,255,255,0.92)",
                    border: "1px solid var(--hairline)",
                    fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        STL · v{asset.stl_version}
      </div>
    </div>
  );
}
